import type { Progress } from "@obean/game-contract";
/**
 * The api, through the same-origin router (ADR 0010): the guest cookie rides along by itself.
 * Every call either returns the parsed body or throws an ApiError with the api's error code.
 */

import type { AdNetwork } from "../ads/network.js";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    /** The rest of the api's reply, e.g. when the next free room comes (`hosting_limit`). */
    readonly body: Record<string, unknown> = {},
  ) {
    super(`${status} ${code}`);
  }
}

/** What a suspended or banned player is told when the api refuses them (`on_hold`, #384). */
export const ON_HOLD =
  "Your account can't open or join rooms right now. If you think that's a mistake, write to hello@obean.com.";

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: body === undefined ? {} : { "content-type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const data = (await res.json().catch(() => ({}))) as { error?: unknown };
  if (!res.ok)
    throw new ApiError(res.status, typeof data.error === "string" ? data.error : "unknown", data);
  return data as T;
}

export interface Guest {
  guestId: string;
  nickname: string | null;
  /** The extra on their bean (ADR 0018); null for plain. */
  bean?: string | null;
  /** The packs they unlock (the Obean Pass's, ADR 0022): from GET /api/me. */
  packs?: string[];
}

/** Free hosting with the Pass switch on (ADR 0022): rooms opened in the last 24 hours. */
export interface Hosting {
  limit: number;
  used: number;
  /** When the next free room comes, if none is free now (ms). */
  nextFreeAt: number | null;
  /** A guest who could sign in for more: that many free rooms a day (ADR 0030). */
  signInFor?: number;
}

/** GET /api/me: the guest, what they unlock, and their Obean Pass (ADR 0022). */
export interface Me extends Guest {
  packs: string[];
  signedIn?: boolean;
  /** Signed in: the person's id, to keep their progress apart in this browser (ADR 0029). */
  personId?: string;
  /** Signed in: whether this browser is the person's own, so its guest progress is theirs. */
  ownBrowser?: boolean;
  /** Whether the Pass is sold here (the switch); off, Obean is free. */
  passOffered?: boolean;
  /** Whether they see ads (ADR 0026): the ADS switch on and no Pass. */
  ads?: boolean;
  /** Which network serves them, and its public ids; absent: Obean's own ads. */
  adNetwork?: AdNetwork;
  /** plan "free": a period Obean gave, with nothing to manage in Stripe (#384). */
  pass?: { plan: "month" | "year" | "free"; periodEnd: number | null; cancelling: boolean } | null;
  hosting?: Hosting | null;
}
export interface Invite {
  inviteId: string;
  url: string;
  expiresAt: number;
}
export type InviteState = "valid" | "expired" | "revoked" | "room_closed";

/** A result of a game's daily, as friends see it (P4-TANK-03). */
export interface DailyResult {
  won: boolean;
  stars: number;
  score: number;
}
/** How everyone did at a game's daily (ADR 0041): counts only. */
export interface DailyEveryone {
  played: number;
  /** How many did less well than the result asked about. */
  below: number;
  /** How many reached each of the five tiers, lowest first. */
  tiers: number[];
}

export interface FriendDaily extends DailyResult {
  id: string;
  name: string;
  bean: string | null;
}

export const api = {
  /** The cookie's guest, or a new one on the first visit (the api returns an existing one). */
  guest: () => call<Guest>("POST", "/api/guests"),
  setNickname: (nickname: string) => call<Guest>("PATCH", "/api/me", { nickname }),
  /** The extra on their bean (ADR 0018), kept for their next rooms; null for plain. */
  setBean: (bean: string | null) => call<Guest>("PATCH", "/api/me", { bean }),
  /** "Delete my data" (P2-SAFE-02): the guest and its name are gone, and the cookie with them. */
  deleteMe: () => call<{ deleted: true }>("DELETE", "/api/me"),
  createRoom: () => call<{ roomId: string; invite: Invite }>("POST", "/api/rooms"),
  newInvite: (roomId: string) => call<{ invite: Invite }>("POST", `/api/rooms/${roomId}/invites`),
  resolveInvite: (roomId: string, secret: string) =>
    call<{
      state: InviteState;
      roomId: string;
      inviter: { nickname: string | null };
      expiresAt: number;
    }>("POST", "/api/invites/resolve", { roomId, secret }),
  /** The cookie's guest, what they unlock, their Pass and free hosting (ADR 0022). */
  me: () => call<Me>("GET", "/api/me"),
  /** Signed-in people only (ADR 0016): friends, and in-app invites between them. */
  friends: () =>
    call<{
      friends: {
        id: string;
        name: string;
        lastPlayedAt: number | null;
        bean?: string | null;
        /** Online now (ADR 0031), and the game they're in while they are. */
        online?: boolean;
        game?: string | null;
        /** Their beans and this week's (ADR 0043); null while they appear offline. */
        beans?: number | null;
        week?: number | null;
      }[];
      mine?: { beans: number; week: number };
    }>("GET", "/api/friends"),
  /** Signed-in people only (P4-TANK-03): my best of a game's daily, and my friends'. */
  dailyBoard: (gameId: string, day: string) =>
    call<{ me: DailyResult | null; friends: FriendDaily[] }>("GET", `/api/daily/${gameId}/${day}`),
  /** Keeps my result of a game's daily if it's my best of that day (signed in). */
  saveDaily: (gameId: string, day: string, result: DailyResult) =>
    call<{ best: boolean }>("PUT", `/api/daily/${gameId}/${day}`, result),
  /** How many have played a game's daily today (ADR 0043 part 5), counting nothing. */
  dailyEveryone: (gameId: string, day: string) =>
    call<DailyEveryone>("GET", `/api/daily/${gameId}/${day}/everyone?tier=0&value=0`),
  /** Counts a play of today's daily (this browser's first of the day counts) and where it falls. */
  countDaily: (gameId: string, day: string, place: { tier: number; value: number }) =>
    call<DailyEveryone & { counted: boolean }>(
      "POST",
      `/api/daily/${gameId}/${day}/everyone`,
      place,
    ),
  /** Signed-in teammates in this room I could add, right after a match (ADR 0031). */
  friendsInRoom: (roomId: string) =>
    call<{ people: { id: string; name: string; asked: boolean }[] }>(
      "GET",
      `/api/friends/room/${roomId}`,
    ),
  /** Ask someone to be friends; `source` says where from, for the numbers. */
  requestFriend: (to: string, source?: "match") =>
    call<{ result: string }>("POST", "/api/friends/requests", {
      to,
      ...(source ? { source } : {}),
    }),
  inviteFriend: (friendId: string, roomId: string) =>
    call<{ id: string }>("POST", `/api/friends/${friendId}/invite`, { roomId }),
  friendInvites: () =>
    call<{ invites: { id: string; roomId: string; fromName: string }[] }>(
      "GET",
      "/api/friends/invites",
    ),
  acceptInvite: (id: string) =>
    call<{ roomId: string }>("POST", `/api/friends/invites/${id}/accept`),
  declineInvite: (id: string) =>
    call<{ result: string }>("POST", `/api/friends/invites/${id}/decline`),
  /** ICE servers for this room's voice (ADR 0008): TURN credentials when the environment has them. */
  /** A signed-in person's progress in a game (ADR 0029). */
  progress: (gameId: string) =>
    call<{ progress: Progress }>("GET", `/api/progress/${encodeURIComponent(gameId)}`),
  /** Merges this device's progress into the person's; the merged record comes back. */
  /** Signed in: this device's beans for the person; their sum over every device comes back (#585). */
  saveBeans: (device: string, total: number) =>
    call<{ total: number }>("PUT", "/api/beans", { device, total }),
  saveProgress: (gameId: string, progress: Progress) =>
    call<{ progress: Progress }>("PUT", `/api/progress/${encodeURIComponent(gameId)}`, progress),
  /** The best stars per mission the room's members earned together (P3-TANK-04). */
  stars: (roomId: string, gameId: string) =>
    call<{ stars: Record<string, number> }>(
      "GET",
      `/api/rooms/${roomId}/stars?game=${encodeURIComponent(gameId)}`,
    ),
  voiceCredentials: (roomId: string) =>
    call<{ iceServers: RTCIceServer[]; turn: boolean }>(
      "POST",
      `/api/rooms/${roomId}/voice-credentials`,
    ),
  roomToken: (roomId: string, secret?: string) =>
    call<{ token: string; expiresAt: number; protocol: string; path: string }>(
      "POST",
      `/api/rooms/${roomId}/token`,
      secret === undefined ? {} : { secret },
    ),
};

/** Over a rate limit (P2-API-05): the api answers 429 for a minute. */
export const TOO_MANY_TRIES = "That's a lot of tries in a minute. Wait a moment, then try again.";

/** Friendly words for the api's refusals of a name (or of a new room). */
export const NICKNAME_ERRORS: Record<string, string> = {
  nickname_length: "Use 2 to 20 characters.",
  nickname_characters: "Use letters, numbers, spaces and . _ ' - only.",
  nickname_blocked: "Please choose another name.",
  rate_limited: TOO_MANY_TRIES,
};
